import { useState, useEffect, useMemo } from "react";
import { fetchPersonnel } from "./personnelSlice";
import { useDispatch } from "react-redux";
import useDebounce from "./useDebounce";
import API_ROUTES from "../../../api-routes";
import apiService from "../../../services/service";
import { getName } from "country-list";
import { useParams } from "react-router-dom";

const usePersonnelTable = (
  personnel,
  personnelState,
  setPersonnelState,
  showKeyPersonnel,
) => {
  const Personnel = useMemo(() => personnel?.data || [], [personnel]);
  const { id } = useParams();
  const [data, setData] = useState();
  const [visible, setVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [rowToEdit, setRowToEdit] = useState(null);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [message, setMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [editMessage, setEditMessage] = useState("");
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [searchDataArr, setSearchDataArr] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState();
  const [searchStateArr, setSearchStateArr] = useState();
  const dispatch = useDispatch();

  useEffect(() => {
    if (Array.isArray(Personnel)) {
      setData((prevData) => {
        if (JSON.stringify(prevData) !== JSON.stringify(Personnel)) {
          return Personnel;
        }
        return prevData;
      });
    } else {
      setData([]);
    }
  }, [Personnel]);

  const filterData = (data, searchTerm, selectedLocation) => {
    let filteredData = data;

    if (searchTerm !== "") {
      const lowercasedFilter = searchTerm.toLowerCase();
      filteredData = filteredData.filter((item) =>
        Object.keys(item).some((key) => {
          const value = item[key];

          if (value !== null && value !== undefined) {
            if (typeof value === "object") {
              return Object.values(value)
                .toString()
                .toLowerCase()
                .includes(lowercasedFilter);
            } else {
              return value.toString().toLowerCase().includes(lowercasedFilter);
            }
          }
          return false;
        }),
      );
    }

    if (selectedLocation) {
      const normalizedLocation = selectedLocation.toLowerCase();

      filteredData = filteredData.filter(
        (item) =>
          item.country &&
          typeof item.country === "string" &&
          item.country.toLowerCase() === normalizedLocation,
      );
    }

    return filteredData;
  };

  const debouncedSearchTerm = useDebounce(searchTerm, 200);

  useEffect(() => {
    if (data) {
      setSearchDataArr(filterData(data, debouncedSearchTerm, selectedLocation));
    }
  }, [debouncedSearchTerm, data, selectedLocation]);

  const filterPersonnelStateData = (
    personnelState,
    searchTerm,
    selectedLocation,
  ) => {
    let filteredData = personnelState;

    if (searchTerm !== "") {
      const lowercasedFilter = searchTerm.toLowerCase();
      filteredData = filteredData.filter((item) =>
        Object.keys(item).some((key) => {
          const value = item[key];

          if (value !== null && value !== undefined) {
            if (typeof value === "object") {
              return Object.values(value)
                .toString()
                .toLowerCase()
                .includes(lowercasedFilter);
            } else {
              return value.toString().toLowerCase().includes(lowercasedFilter);
            }
          }
          return false;
        }),
      );
    }

    if (selectedLocation) {
      const normalizedLocation = selectedLocation.toLowerCase();

      filteredData = filteredData.filter(
        (item) =>
          item.country &&
          typeof item.country === "string" &&
          item.country.toLowerCase() === normalizedLocation,
      );
    }

    return filteredData;
  };

  useEffect(() => {
    if (personnelState) {
      setSearchStateArr(
        filterPersonnelStateData(
          personnelState,
          debouncedSearchTerm,
          selectedLocation,
        ),
      );
    }
  }, [debouncedSearchTerm, personnelState, selectedLocation]);

  const onPageChange = (event) => {
    setFirst(event.first);
    setRows(event.rows);
  };

  useEffect(() => {
    if (data && !showKeyPersonnel) {
      const locations = [
        ...new Set(
          data
            .map((item) => item.country?.toUpperCase())
            .filter((location) => location !== undefined),
        ),
      ].map((location) => ({ label: getName(location), value: location }));
      setLocation(locations);
    } else if (personnelState) {
      const locations = [
        ...new Set(
          personnelState
            .map((item) => item.country?.toUpperCase())
            .filter((location) => location !== undefined),
        ),
      ].map((location) => ({ label: getName(location), value: location }));
      setLocation(locations);
    }
  }, [data, personnelState]);

  const showEditDialog = (rowData) => {
    setRowToEdit(rowData);
    setEditVisible(true);
  };

  const showAddDialog = () => {
    setIsAddMode(true);
    setRowToEdit(null);
    setEditVisible(true);
  };

  const showDeleteDialog = (rowData) => {
    setRowToDelete(rowData);
    setVisible(true);
  };

  const deleteRow = async () => {
    try {
      if (!showKeyPersonnel) {
        const data = await apiService(API_ROUTES.DELETE_PERSONNEL, "DELETE", {
          replace: { id: rowToDelete.id },
        });

        if (data.status) {
          dispatch(fetchPersonnel({ id, skipCache: true })); // GXM-1769: skipCache after delete
          setMessage("Your new contact has been deleted.");
        } else {
          setMessage(data.message || "Failed to delete contact.");
        }
      } else {
        setPersonnelState((prevState) =>
          prevState.filter((person) => person.id !== rowToDelete.id),
        );

        setMessage("Your new contact has been deleted.");
      }
    } catch (error) {
      console.error("Error deleting contact:", error);
      setMessage("An error occurred while deleting contact.");
    }
    setVisible(false);
  };

  return {
    data,
    visible,
    setVisible,
    editVisible,
    setEditVisible,
    isAddMode,
    rowToEdit,
    setRowToEdit,
    rowToDelete,
    setRowToDelete,
    message,
    setMessage,
    successMessage,
    setSuccessMessage,
    first,
    rows,
    onPageChange,
    showEditDialog,
    showAddDialog,
    showDeleteDialog,
    deleteRow,
    searchDataArr,
    setSearchDataArr,
    searchTerm,
    setSearchTerm,
    loading,
    setLoading,
    editMessage,
    setEditMessage,
    location,
    setLocation,
    selectedLocation,
    setSelectedLocation,
    searchStateArr,
  };
};

export default usePersonnelTable;
